import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import {
  OfflineEntry, OfflineEntryType, SyncStatus,
  addOfflineEntry, getAllEntries, getEntriesByStatus,
  updateEntry, deleteEntry, clearSyncedEntries, getStats,
} from "@/lib/offline/db";
import { useToast } from "@/hooks/use-toast";

interface SyncStats {
  pending: number;
  synced: number;
  failed: number;
  conflict: number;
  total: number;
}

interface OfflineContextType {
  isOnline: boolean;
  entries: OfflineEntry[];
  stats: SyncStats;
  isSyncing: boolean;
  conflicts: OfflineEntry[];
  saveOffline: (type: OfflineEntryType, data: Record<string, unknown>) => Promise<OfflineEntry>;
  syncAll: () => Promise<void>;
  resolveConflict: (id: string, resolution: "local" | "cloud" | "merge") => Promise<void>;
  retryFailed: () => Promise<void>;
  clearSynced: () => Promise<void>;
  refreshEntries: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | null>(null);

export function OfflineProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [entries, setEntries] = useState<OfflineEntry[]>([]);
  const [stats, setStats] = useState<SyncStats>({ pending: 0, synced: 0, failed: 0, conflict: 0, total: 0 });
  const [isSyncing, setIsSyncing] = useState(false);
  const [conflicts, setConflicts] = useState<OfflineEntry[]>([]);

  const refreshEntries = useCallback(async () => {
    const [all, st, conf] = await Promise.all([
      getAllEntries(),
      getStats(),
      getEntriesByStatus("conflict"),
    ]);
    setEntries(all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    setStats(st);
    setConflicts(conf);
  }, []);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      toast({ title: "🟢 Back Online", description: "Connection restored. Starting sync..." });
    };
    const goOffline = () => {
      setIsOnline(false);
      toast({ title: "🔴 Offline Mode", description: "Working offline. Entries will sync when connection returns.", variant: "destructive" });
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [toast]);

  // Initial load + auto-sync when back online
  useEffect(() => {
    refreshEntries();
  }, [refreshEntries]);

  useEffect(() => {
    if (isOnline) {
      // Auto-sync pending entries after 2s delay
      const timer = setTimeout(() => {
        syncAll();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOffline = useCallback(async (type: OfflineEntryType, data: Record<string, unknown>) => {
    const entry = await addOfflineEntry({ type, data });
    await refreshEntries();
    toast({ title: "Saved Offline", description: `${type} entry saved locally. Will sync when online.` });
    return entry;
  }, [refreshEntries, toast]);

  const syncAll = useCallback(async () => {
    const pending = await getEntriesByStatus("pending");
    if (pending.length === 0) return;
    setIsSyncing(true);

    let syncedCount = 0;
    let failedCount = 0;

    for (const entry of pending) {
      try {
        // Simulate cloud sync — in production, this calls the actual API
        await new Promise((resolve) => setTimeout(resolve, 300 + Math.random() * 500));

        // 5% chance of conflict for demo purposes
        if (Math.random() < 0.05) {
          await updateEntry(entry.id, {
            status: "conflict" as SyncStatus,
            conflictData: {
              localVersion: entry.data,
              cloudVersion: { ...entry.data, _cloudModified: true, _cloudTimestamp: new Date().toISOString() },
            },
          });
          failedCount++;
        } else {
          await updateEntry(entry.id, { status: "synced" as SyncStatus });
          syncedCount++;
        }
      } catch (err: unknown) {
        await updateEntry(entry.id, {
          status: "failed" as SyncStatus,
          syncAttempts: entry.syncAttempts + 1,
          lastSyncError: err instanceof Error ? err.message : "Unknown error",
        });
        failedCount++;
      }
    }

    setIsSyncing(false);
    await refreshEntries();

    if (syncedCount > 0) {
      toast({ title: "Sync Complete", description: `${syncedCount} entries synced to cloud.` });
    }
    if (failedCount > 0) {
      toast({ title: "Sync Issues", description: `${failedCount} entries need attention.`, variant: "destructive" });
    }
  }, [refreshEntries, toast]);

  const resolveConflict = useCallback(async (id: string, resolution: "local" | "cloud" | "merge") => {
    const entry = entries.find((e) => e.id === id);
    if (!entry || !entry.conflictData) return;

    let resolvedData = entry.data;
    if (resolution === "cloud") {
      resolvedData = entry.conflictData.cloudVersion;
    } else if (resolution === "merge") {
      resolvedData = { ...entry.conflictData.cloudVersion, ...entry.data };
    }

    await updateEntry(id, {
      status: "synced" as SyncStatus,
      data: resolvedData,
      conflictData: undefined,
    });
    await refreshEntries();
    toast({ title: "Conflict Resolved", description: `Applied ${resolution} version.` });
  }, [entries, refreshEntries, toast]);

  const retryFailed = useCallback(async () => {
    const failed = await getEntriesByStatus("failed");
    for (const entry of failed) {
      await updateEntry(entry.id, { status: "pending" as SyncStatus });
    }
    await refreshEntries();
    if (isOnline) syncAll();
  }, [refreshEntries, isOnline, syncAll]);

  const clearSynced = useCallback(async () => {
    const count = await clearSyncedEntries();
    await refreshEntries();
    toast({ title: "Cleared", description: `${count} synced entries removed from local storage.` });
  }, [refreshEntries, toast]);

  return (
    <OfflineContext.Provider value={{
      isOnline, entries, stats, isSyncing, conflicts,
      saveOffline, syncAll, resolveConflict, retryFailed, clearSynced, refreshEntries,
    }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error("useOffline must be used within OfflineProvider");
  return ctx;
}
